import { useRef, useState } from "react";
import html2canvas from "html2canvas";
import { Award, Car, Download, Share2, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { monthLabel } from "@/lib/dpc";
import { fmtTd } from "./TdFleet";

const fmtDate = (iso) => new Date(iso + "T00:00:00").toLocaleDateString("en-IN", { day: "2-digit", month: "short" });

// Shareable award card for the group's best test-drive fleet productivity (winner of the month; running month = current leader).
export function FleetAwardCard({ a }) {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);
  if (!a) return null;
  const headline = a.running ? "Current leader · month to date" : `Winner · ${monthLabel(a.month)}`;
  const summary = `${a.title} — ${monthLabel(a.month)}${a.running ? ` (as of ${fmtDate(a.as_of)})` : ""}: ${a.dealer_code} ${a.dealer_name} · ${fmtTd(a.value)} TDs/car/day · ${a.test_drives} test drives on ${a.td_cars} TD cars over ${a.working_days} working days · group ${fmtTd(a.group_value)}`;

  const render = async () => {
    const canvas = await html2canvas(ref.current, { scale: 2, backgroundColor: null, useCORS: true });
    return new Promise((res) => canvas.toBlob(res, "image/png"));
  };
  const download = async () => {
    setBusy(true);
    try {
      const blob = await render();
      const url = URL.createObjectURL(blob);
      const el = document.createElement("a");
      el.download = `td-fleet-award_${a.dealer_code}_${a.month}.png`;
      el.href = url;
      el.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error("Could not render the card"); } finally { setBusy(false); }
  };
  const share = async () => {
    setBusy(true);
    try {
      const blob = await render();
      const file = new File([blob], `td-fleet-award_${a.dealer_code}_${a.month}.png`, { type: "image/png" });
      if (navigator.canShare?.({ files: [file] })) {
        await navigator.share({ files: [file], title: a.title, text: summary });
      } else {
        await navigator.clipboard.writeText(summary);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
        toast.success("Award text copied — paste it into WhatsApp or mail");
      }
    } catch (e) { if (e?.name !== "AbortError") toast.error("Sharing not available here — use Download"); } finally { setBusy(false); }
  };

  return (
    <div className="space-y-2" data-testid="td-award">
      <div ref={ref} className="relative overflow-hidden rounded-xl bg-[#0b1220] text-white p-6 sm:p-8 w-full max-w-xl" data-testid="td-award-card">
        <div className="absolute -right-16 -top-16 h-56 w-56 rounded-full bg-amber-400/15" />
        <div className="absolute -left-10 -bottom-20 h-48 w-48 rounded-full bg-white/5" />
        <div className="relative flex items-center justify-between">
          <span className="text-[10px] uppercase tracking-[0.25em] text-white/60">Incheon Kia · Dealer Performance Cockpit</span>
          <Award size={22} className="text-amber-400" />
        </div>
        <div className="relative mt-5 text-[11px] uppercase tracking-widest text-amber-300" data-testid="td-award-headline">{headline}</div>
        <div className="relative font-heading text-xl sm:text-2xl font-extrabold leading-tight mt-1">{a.title}</div>
        <div className="relative mt-5 flex items-end gap-4">
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-widest text-white/60">Awarded to</div>
            <div className="font-heading text-2xl sm:text-3xl font-extrabold truncate" data-testid="td-award-dealer">{a.dealer_code} <span className="text-white/90">{a.dealer_name}</span></div>
          </div>
        </div>
        <div className="relative mt-6 grid grid-cols-3 gap-3">
          <div className="rounded-lg bg-white/10 px-3 py-2">
            <div className="text-[9px] uppercase tracking-widest text-white/60">TDs / car / day</div>
            <div className="num text-2xl font-extrabold text-amber-300" data-testid="td-award-value">{fmtTd(a.value)}</div>
          </div>
          <div className="rounded-lg bg-white/10 px-3 py-2">
            <div className="text-[9px] uppercase tracking-widest text-white/60">Test drives</div>
            <div className="num text-2xl font-extrabold">{a.test_drives}</div>
            <div className="text-[9px] text-white/60">{a.working_days} working days</div>
          </div>
          <div className="rounded-lg bg-white/10 px-3 py-2">
            <div className="text-[9px] uppercase tracking-widest text-white/60 inline-flex items-center gap-1"><Car size={9} /> TD cars</div>
            <div className="num text-2xl font-extrabold">{a.td_cars}</div>
            <div className="text-[9px] text-white/60">group {fmtTd(a.group_value)} /car/day</div>
          </div>
        </div>
        <div className="relative mt-5 flex items-center justify-between text-[10px] text-white/60">
          <span>{monthLabel(a.month)}{a.running ? ` · as of ${fmtDate(a.as_of)}` : ""} · {a.eligible} eligible dealer{a.eligible === 1 ? "" : "s"}</span>
          {a.runner_up && <span>Runner-up {a.runner_up.dealer_code} · {fmtTd(a.runner_up.value)}</span>}
        </div>
      </div>
      <div className="flex items-center gap-2">
        <Button size="sm" variant="outline" onClick={download} disabled={busy} data-testid="td-award-download"><Download size={14} className="mr-1" /> Download PNG</Button>
        <Button size="sm" onClick={share} disabled={busy} data-testid="td-award-share">{copied ? <Check size={14} className="mr-1" /> : <Share2 size={14} className="mr-1" />} {copied ? "Copied" : "Share"}</Button>
        <span className="text-[10px] text-muted-foreground">{a.running ? "Updates daily until the month closes." : "Issued at month close."}</span>
      </div>
    </div>
  );
}
